import React, { useContext, useMemo } from "react";
import { Collapse, Icon, Tag, Tooltip } from "antd";
import * as R from "ramda";
import styled, { ThemeContext } from "styled-components";

import CandidateVideo from "../CandidateVideo";

const customPanelStyle = {
  background: "#f7f7f7",
  borderRadius: 4,
  marginBottom: 24,
  overflow: "hidden"
};

const Container = styled.div`
  margin-right: 33px;
`;

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    background: transparent !important;

    &:not(:last-child) {
      border-bottom: 1px solid #e8e8e8;
    }

    &:last-child {
      border-bottom: 0px;
    }
  }
`;

const StyledPanel = styled(Collapse.Panel)`
  .ant-collapse-content-box,
  .ant-collapse-header {
    padding: 16px 10px 16px 49px !important;
  }
`;

const AnswerVideo = styled.div`
  video {
    max-height: 350px;
  }
`;

const AnswerHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  .anticon.primary {
    font-size: 20px;
    color: ${({ theme }) => theme["@primary"]};
  }

  .anticon.tooltip {
    font-size: 16px;
    color: ${({ theme }) => theme["@gray-80"]};
    margin-left: 6px;
  }
`;

const AnswerTitle = styled.span`
  color: ${({ theme }) => theme["@gray-80"]};
  font-size: 17px;
  font-weight: bold;
`;

const AnswerText = styled.p`
  margin-bottom: 8px;
`;

const ChatbotTags = styled.div`
  &:not(:first-child) {
    border-top: ${({ theme }) => theme["@gray-40"]} solid 1px;
  }
  display: block;
  margin-top: 1rem;
  padding: 1rem 0;
`;

const iconsByType = {
  video: "video-camera",
  number: "number",
  multiple: "check-square",
  chatbot: "message-o",
  referral: "usergroup-add",
  salary: "dollar"
};

const answerTypes = R.keys(iconsByType);

const isDocumentUploadAnswer = answer =>
  answer.type === "document_upload" ||
  String(answer.idQuestion || "").startsWith("document-upload-");

const getUniqueAnswers = answers => {
  let uniqueAnswers = [];
  answers.forEach((answer, i) => {
    if (
      !R.has("idQuestion", answer) ||
      !answers
        .slice(i + 1)
        .find(({ idQuestion }) => idQuestion === answer.idQuestion)
    ) {
      return uniqueAnswers.push(answer);
    }
  });
  return uniqueAnswers;
};

const Answer = ({ answer, chatbotTags, t }) => {
  if (answer.video) {
    return (
      <AnswerVideo>
        <CandidateVideo video={answer.video} controls />
      </AnswerVideo>
    );
  }

  if (answer.type === "chatbot") {
    return (
      <div>
        <AnswerText>
          {!answer.opportunity
            ? t("opportunity:process.candidateDetails.chatbotUnanswered")
            : (R.isEmpty(chatbotTags) ||
                R.propSatisfies(
                  R.isEmpty,
                  "chatbotTags",
                  R.find(R.propEq("title", "Screening"), chatbotTags)
                )) &&
              t("opportunity:process.candidateDetails.chatbotAnswered")}
        </AnswerText>
        {chatbotTags.map(
          ({ title, chatbotTags: tags }, i) =>
            tags &&
            tags.length > 0 && (
              <ChatbotTags key={`${title}-${i}`}>
                <AnswerText>{title}</AnswerText>
                {tags.map((tag, index) => (
                  <Tag key={`${title}-tagt-${tag}-${index}`}>{tag}</Tag>
                ))}
              </ChatbotTags>
            )
        )}
      </div>
    );
  }
  if (answer.type === "referral") {
    return (
      <span>
        {answer.answer.wasReferred
          ? `${t("opportunity:screening.referredBy")} ${answer.answer.name} (${
              answer.answer.email
            }).`
          : t("opportunity:screening.noReferral")}
      </span>
    );
  }

  if (!answer.opportunity || !answerTypes.includes(answer.type)) {
    return t("opportunity:process.candidateDetails.noAnswer");
  }

  if (
    (answer.type === "number" || answer.type === "multiple") &&
    !R.isNil(answer.answer)
  ) {
    return <span>{answer.answer}</span>;
  }

  if (answer.type === "salary" && !R.isNil(answer.answer)) {
    return (
      <span>
        {`$ ${answer.answer || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
      </span>
    );
  }
};

export default function Answers({ answers, chatbotTags = [], extraPanels = [], t }) {
  const theme = useContext(ThemeContext);
  const uniqueAnswers = useMemo(() => {
    const uniqueAnswerValues = getUniqueAnswers(
      answers.filter(answer => !isDocumentUploadAnswer(answer))
    );
    const hasAnsweredChatbot = R.compose(
      R.includes("chatbot"),
      R.map(R.prop("type"))
    )(uniqueAnswerValues);
    return hasAnsweredChatbot
      ? uniqueAnswerValues
      : R.compose(
          R.not,
          R.isEmpty,
          R.flatten,
          R.map(R.prop("chatbotTags"))
        )(chatbotTags)
      ? [{ type: "chatbot" }, ...uniqueAnswerValues]
      : uniqueAnswerValues;
  }, [answers, chatbotTags]);

  return (
    <Container>
      <StyledCollapse
        expandIcon={({ isActive }) => (
          <Icon
            type="right"
            style={{ fontSize: "16px", color: theme["@primary"], left: 23 }}
            theme="outlined"
            rotate={isActive ? 90 : 0}
          />
        )}
        bordered={false}
        accordion
      >
        {uniqueAnswers.map((answer, key) => (
          <StyledPanel
            header={
              <AnswerHeader data-testid="answer-header">
                <div>
                  <AnswerTitle>
                    {R.includes(answer.type, ["referral", "salary"])
                      ? t(`opportunity:screening.${answer.type}`)
                      : answer.question ||
                        (answer.type === "chatbot" &&
                          t(
                            "opportunity:process.candidateDetails.chatbotScreening"
                          ))}
                  </AnswerTitle>
                  {answer.type === "chatbot" && (
                    <Tooltip
                      placement="top"
                      title={t(
                        "opportunity:process.candidateDetails.chatbotTagsInfo"
                      )}
                    >
                      <Icon className="tooltip" type="info-circle" />
                    </Tooltip>
                  )}
                </div>
                <Icon
                  className="primary"
                  type={iconsByType[answer.type || "video"]}
                />
              </AnswerHeader>
            }
            key={`${answer.idQuestion}-${key}`}
            style={customPanelStyle}
          >
            <Answer answer={answer} chatbotTags={chatbotTags} t={t} />
          </StyledPanel>
        ))}
        {extraPanels.map(panel => (
          <StyledPanel
            header={
              <AnswerHeader data-testid={`${panel.key}-header`}>
                <div>
                  <AnswerTitle>{panel.title}</AnswerTitle>
                </div>
                <Icon
                  className="primary"
                  type={panel.icon || "file-done"}
                />
              </AnswerHeader>
            }
            key={panel.key}
            style={customPanelStyle}
          >
            {panel.content}
          </StyledPanel>
        ))}
      </StyledCollapse>
    </Container>
  );
}
